import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCart, ZAR } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { CHECKOUT } from "@/constants/testIds";
import TrustBar from "@/components/TrustBar";
import DeliveryETA from "@/components/DeliveryETA";
import FreeShipProgress from "@/components/FreeShipProgress";
import CheckoutCrossSell from "@/components/CheckoutCrossSell";

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();

  const [form, setForm] = useState({
    customer_first_name: "",
    customer_last_name: "",
    customer_email: user?.email || "",
    customer_phone: "",
    shipping_address: "",
  });
  const [discount, setDiscount] = useState("");
  const [validated, setValidated] = useState(null);
  const [points, setPoints] = useState(0);
  const [userPoints, setUserPoints] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [threshold, setThreshold] = useState(500);

  useEffect(() => {
    api.get("/content").then((r) => setThreshold(r.data.free_shipping_threshold || 500)).catch(() => {});
    // Fire InitiateCheckout event
    try {
      const value = subtotal || 0;
      if (typeof window.gtag === "function") {
        window.gtag("event", "begin_checkout", {
          currency: "ZAR",
          value,
          items: items.map((it) => ({
            item_id: it.product_slug,
            item_name: it.name,
            price: it.unit_price,
            quantity: it.quantity,
          })),
        });
      }
      if (typeof window.fbq === "function") {
        window.fbq("track", "InitiateCheckout", {
          value,
          currency: "ZAR",
          num_items: items.reduce((s, i) => s + i.quantity, 0),
          content_ids: items.map((i) => i.product_slug),
        });
      }
    } catch {
      /* swallow */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user) {
      api.get("/auth/me").then((r) => setUserPoints(r.data.points || 0));
      // Autofill from saved profile
      api.get("/auth/me").then((r) => {
        const p = r.data || {};
        setForm((f) => ({
          ...f,
          customer_email: user.email,
          customer_phone: f.customer_phone || p.phone || "",
          shipping_address: f.shipping_address || p.shipping_address || "",
          customer_first_name: f.customer_first_name || (p.name || "").split(" ")[0] || "",
          customer_last_name:
            f.customer_last_name ||
            (p.name || "").split(" ").slice(1).join(" ") ||
            "",
        }));
      });
    }
  }, [user]);

  useEffect(() => {
    if (items.length === 0) nav("/cart");
  }, [items.length, nav]);

  const applyDiscount = async () => {
    if (!discount) return;
    try {
      const { data } = await api.get(`/discounts/${discount.trim().toUpperCase()}`);
      setValidated(data);
      toast.success(`Code applied: ${data.code}`);
    } catch {
      setValidated(null);
      toast.error("Invalid or expired code");
    }
  };

  const discountValue =
    !validated
      ? 0
      : validated.percent_off
      ? subtotal * (validated.percent_off / 100)
      : validated.amount_off || 0;
  const capped = Math.min(discountValue, subtotal);
  const pointsValue = (Math.min(points, userPoints) / 100) * 10; // 100 pts = R10
  const total = Math.max(0, subtotal - capped - pointsValue);

  const pay = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    // Save cart snapshot for abandonment recovery (fire-and-forget)
    api
      .post("/cart/save", {
        email: form.customer_email,
        items: items.map((i) => ({
          name: i.name,
          unit_price: i.unit_price,
          quantity: i.quantity,
          variant: i.variant || null,
        })),
      })
      .catch(() => {});
    try {
      // create order
      const { data: order } = await api.post("/orders", {
        items: items.map((i) => ({
          product_slug: i.product_slug,
          name: i.name,
          unit_price: i.unit_price,
          quantity: i.quantity,
          variant: i.variant || null,
          image: i.image || null,
        })),
        ...form,
        discount_code: validated?.code || null,
        points_to_redeem: user ? Math.min(points, userPoints) : 0,
      });
      // initiate payfast
      const { data: pf } = await api.post(`/payfast/initiate/${order.order_id}`);
      // Build hidden form + submit. Cart is intentionally kept in localStorage
      // until CheckoutSuccess confirms payment (defense-in-depth if the
      // redirect fails).
      const f = document.createElement("form");
      f.method = "POST";
      f.action = pf.process_url;
      Object.entries(pf.fields).forEach(([k, v]) => {
        const inp = document.createElement("input");
        inp.type = "hidden";
        inp.name = k;
        inp.value = v ?? "";
        f.appendChild(inp);
      });
      document.body.appendChild(f);
      f.submit();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed");
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-[1200px] px-4 py-10 sm:px-6 sm:py-16 lg:px-10">
      <div className="font-label text-xs text-[#FFC900]">/ CHECKOUT</div>
      <h1 className="mt-3 font-display text-3xl font-black tracking-tighter sm:text-5xl">Almost yours.</h1>

      <div className="mt-8 sm:mt-12 grid gap-8 lg:gap-12 lg:grid-cols-3">
        <form onSubmit={pay} className="lg:col-span-2 space-y-6 sm:space-y-8">
          <TrustBar />
          <DeliveryETA />
          <CheckoutProgress
            subtotal={subtotal}
            threshold={threshold}
          />
          <fieldset className="border border-white/10 bg-[#0d0d0d] p-4 sm:p-6">
            <legend className="px-2 font-label text-xs text-[#00FFC2]">1 · Contact</legend>
            <div className="grid gap-4 md:grid-cols-2">
              <input
                data-testid={CHECKOUT.firstName}
                required
                placeholder="First name"
                value={form.customer_first_name}
                onChange={(e) => setForm({ ...form, customer_first_name: e.target.value })}
                className="border border-white/15 bg-black px-4 py-3 text-sm outline-none focus:border-[#FF5A1F]"
              />
              <input
                data-testid={CHECKOUT.lastName}
                required
                placeholder="Last name"
                value={form.customer_last_name}
                onChange={(e) => setForm({ ...form, customer_last_name: e.target.value })}
                className="border border-white/15 bg-black px-4 py-3 text-sm outline-none focus:border-[#FF5A1F]"
              />
              <input
                data-testid={CHECKOUT.email}
                type="email"
                required
                placeholder="Email"
                value={form.customer_email}
                onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                className="border border-white/15 bg-black px-4 py-3 text-sm outline-none focus:border-[#FF5A1F]"
              />
              <input
                data-testid={CHECKOUT.phone}
                placeholder="Phone (optional)"
                value={form.customer_phone}
                onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
                className="border border-white/15 bg-black px-4 py-3 text-sm outline-none focus:border-[#FF5A1F]"
              />
            </div>
          </fieldset>

          <fieldset className="border border-white/10 bg-[#0d0d0d] p-4 sm:p-6">
            <legend className="px-2 font-label text-xs text-[#00FFC2]">2 · Shipping</legend>
            <textarea
              data-testid={CHECKOUT.address}
              required
              rows={3}
              placeholder="Full delivery address including postal code"
              value={form.shipping_address}
              onChange={(e) => setForm({ ...form, shipping_address: e.target.value })}
              className="w-full border border-white/15 bg-black px-4 py-3 text-sm outline-none focus:border-[#FF5A1F]"
            />
          </fieldset>

          <fieldset className="border border-white/10 bg-[#0d0d0d] p-4 sm:p-6">
            <legend className="px-2 font-label text-xs text-[#00FFC2]">3 · Save more</legend>
            <div className="flex gap-2">
              <input
                data-testid={CHECKOUT.discount}
                placeholder="Discount code"
                value={discount}
                onChange={(e) => setDiscount(e.target.value)}
                className="flex-1 border border-white/15 bg-black px-4 py-3 text-sm uppercase outline-none focus:border-[#FF5A1F]"
              />
              <button
                type="button"
                data-testid={CHECKOUT.applyDiscount}
                onClick={applyDiscount}
                className="border border-white/20 px-6 font-label text-xs hover:border-[#00FFC2] hover:text-[#00FFC2] transition-colors"
              >
                Apply
              </button>
            </div>
            {validated && (
              <div className="mt-3 text-xs text-[#00FFC2]">
                {validated.percent_off
                  ? `${validated.percent_off}% off applied`
                  : `${ZAR(validated.amount_off)} off applied`}
              </div>
            )}

            {user && userPoints > 0 && (
              <div className="mt-6 border-t border-white/10 pt-6">
                <div className="mb-2 flex items-center justify-between">
                  <div className="font-label text-xs text-[#FFC900]">
                    Redeem loyalty points (Available: {userPoints})
                  </div>
                  <div className="text-xs text-white/50">100 pts = R10</div>
                </div>
                <input
                  data-testid={CHECKOUT.redeemPoints}
                  type="number"
                  min={0}
                  max={userPoints}
                  step={100}
                  value={points}
                  onChange={(e) => setPoints(Math.max(0, Math.min(userPoints, Number(e.target.value) || 0)))}
                  className="w-full border border-white/15 bg-black px-4 py-3 text-sm outline-none focus:border-[#FFC900]"
                />
              </div>
            )}
          </fieldset>

          <CheckoutCrossSell />

          <button
            data-testid={CHECKOUT.payBtn}
            disabled={submitting}
            className="w-full bg-[#FF5A1F] py-4 font-label text-xs text-black transition-transform hover:-translate-y-0.5 disabled:opacity-60"
          >
            {submitting ? "Redirecting to PayFast..." : `Pay ${ZAR(total)} with PayFast`}
          </button>
          <div className="text-center text-[10px] font-label text-white/40">
            Sandbox mode · You will be redirected to PayFast's secure page
          </div>
        </form>

        <aside className="border border-white/10 bg-[#0d0d0d] p-5 sm:p-6 h-fit lg:sticky lg:top-24 space-y-4">
          <div className="font-label text-xs text-white/50">ORDER</div>
          <div className="space-y-3 text-sm">
            {items.map((i, idx) => (
              <div key={idx} className="flex justify-between text-white/70">
                <span>
                  {i.name} <span className="text-white/40">×{i.quantity}</span>
                </span>
                <span>{ZAR(i.unit_price * i.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-white/10 pt-3 space-y-1 text-sm">
            <div className="flex justify-between text-white/60">
              <span>Subtotal</span>
              <span data-testid={CHECKOUT.summarySubtotal}>{ZAR(subtotal)}</span>
            </div>
            {capped > 0 && (
              <div className="flex justify-between text-[#00FFC2]">
                <span>Discount</span>
                <span>-{ZAR(capped)}</span>
              </div>
            )}
            {pointsValue > 0 && (
              <div className="flex justify-between text-[#FFC900]">
                <span>Points redeemed</span>
                <span>-{ZAR(pointsValue)}</span>
              </div>
            )}
          </div>
          <div className="flex items-baseline justify-between border-t border-white/10 pt-4">
            <span className="font-label text-xs text-white/50">TOTAL</span>
            <span data-testid={CHECKOUT.summaryTotal} className="font-display text-2xl font-black text-[#00FFC2]">
              {ZAR(total)}
            </span>
          </div>
          <div className="text-xs text-[#FFC900]">
            You'll earn ~{Math.max(0, Math.round((total / 1.15) * 0.05))} points on this order (5% ex-VAT)
          </div>
        </aside>
      </div>

      {/* Sticky mobile pay bar */}
      <div
        data-testid="checkout-sticky-pay"
        className="lg:hidden fixed inset-x-0 bottom-0 z-40 border-t border-white/15 bg-black/95 backdrop-blur px-4 py-3 shadow-[0_-10px_30px_rgba(0,0,0,0.6)]"
      >
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="font-label text-[9px] tracking-widest text-white/50">TOTAL</div>
            <div className="font-display text-lg font-black text-[#00FFC2] leading-none">
              {ZAR(total)}
            </div>
          </div>
          <button
            type="button"
            onClick={pay}
            disabled={submitting}
            data-testid="checkout-sticky-pay-btn"
            className="flex-1 bg-[#FF5A1F] py-3 font-label text-[11px] text-black transition-transform hover:-translate-y-0.5 disabled:opacity-60"
          >
            {submitting ? "..." : `PAY WITH PAYFAST`}
          </button>
        </div>
      </div>
      {/* Spacer so the sticky bar doesn't cover last content on mobile */}
      <div className="h-20 lg:hidden" aria-hidden />
    </div>
  );
}

function CheckoutProgress({ subtotal, threshold }) {
  return (
    <div className="grid gap-3">
      {/* Free-shipping bar */}
      <FreeShipProgress subtotal={subtotal} threshold={threshold} />
    </div>
  );
}
